import { useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { useApiQuery } from '@/api/ApiQueryServiceProvider'
import { useQueryClient } from '@tanstack/react-query'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Loader2, MapPin, ArrowLeft, Wifi, CheckCircle2, Circle, Radio, Download,
} from 'lucide-react'
import { ProfileHeader, ProfileSection } from '@/components/sensnet/ProfileLayout'
import type { GetNetworkSitesResponse, NetworkSite, NetworkTechnology } from '@/types/sensNet'
import type { GetNetworkCorridorsResponse } from '@/types/sensNet'
import { AppLogger } from '@/lib/logger/appLogger'

const log = AppLogger.getLogger(import.meta)

interface NetworkSiteDetailProps {
  siteNumber: number
}

const TECH_COLORS: Record<string, string> = {
  '4G': 'bg-blue-600 text-white',
  '5G': 'bg-purple-600 text-white',
  'LoRa': 'bg-green-600 text-white',
  'Wifi': 'bg-gray-500 text-white',
}

const BACKHAUL_DISPLAY = [
  { key: 'fiber',              label: 'Fiber',                      detail: '10 Gbps Fiber Connection', role: 'Primary Backhaul',     icon: '⚡' },
  { key: 'wireless microwave', label: 'Wireless Microwave Backhaul', detail: '1 Gbps Microwave Link',    role: 'Backup Backhaul',       icon: '📡' },
  { key: 'mesh',               label: 'Mesh',                       detail: 'Not Available',             role: 'Future Consideration', icon: '🔗' },
]

// ── Main component ───────────────────────────────────────────────────────────

export function NetworkSiteDetail({ siteNumber }: NetworkSiteDetailProps) {
  const { data, isLoading, isError } = useApiQuery<GetNetworkSitesResponse>('getNetworkSites', {})
  const { data: corridorsData } = useApiQuery<GetNetworkCorridorsResponse>('getNetworkCorridors', {})
  const queryClient = useQueryClient()

  const site: NetworkSite | undefined = useMemo(() => {
    const raw: any[] = ((data as any)?.content?.networkSites as any[]) || (data as any)?.networkSites || []
    const match: any = raw.find((s) => s.networkSiteNumber === siteNumber)
    log.info('NetworkSiteDetail: raw API match', {
      hasSite: !!match,
      matchKeys: match ? Object.keys(match) : [],
      sitePictures: match?.sitePictures,
    })
    if (!match) return undefined
    return {
      id: match.id,
      networkSiteNumber: match.networkSiteNumber,
      corridorId: match.corridorId || match.networkCorridorNumber?.toString() || '',
      name: match.name || match.networkSiteName || '',
      siteAddress: match.siteAddress || match.networkSiteAddress || '',
      siteGeoLocation: match.siteGeoLocation || match.location || { latitude: 0, longitude: 0 },
      locationNotes: match.locationNotes || '',
      indoorOrOutdoorSite: match.indoorOrOutdoorSite || 'outdoor',
      verticalStructureHeight: match.verticalStructureHeight,
      outdoorVerticalStructureType: match.outdoorVerticalStructureType,
      indoorStructureType: match.indoorStructureType,
      networkTechnologies: match.networkSiteTechnologies || match.networkTechnologies || [],
      backhaulCapability: match.backhaulCapability || 'fiber',
      shelterAvailable: match.shelterAvailable || false,
      cabinetAttached: match.cabinetAttached || false,
      sitePictures: match.sitePictures || [],
      networkSiteLaunchDate: match.networkSiteLaunchDate,
      createdAt: match.createdAt ?? 0,
      updatedAt: match.updatedAt ?? 0,
    }
  }, [data, siteNumber])

  // Fall back to the dedicated per-site image cache (set by the composer's onSuccess)
  // when the backend hasn't returned pictures yet (e.g., immediately after creation).
  const effectivePictures = useMemo(() => {
    if ((site?.sitePictures?.length ?? 0) > 0) return site!.sitePictures
    return queryClient.getQueryData<string[]>(['site-images', siteNumber]) ?? []
  }, [site, siteNumber, queryClient])

  const corridorName = useMemo(() => {
    if (!site || !corridorsData) return ''
    const corridors: any[] = ((corridorsData as any)?.content?.networkCorridors as any[]) || (corridorsData as any)?.networkCorridors || []
    const corridor = corridors.find(
      (c) =>
        c.networkCorridorNumber?.toString() === site.corridorId
    )
    return corridor?.networkCorridorName || ''
  }, [site, corridorsData])

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError || !site) {
    log.warn('NetworkSiteDetail: site not found', { siteNumber })
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-destructive">Network site not found.</p>
        <Link to="/sensnet/network-sites" className="text-sm text-primary underline">
          Back to Network Sites
        </Link>
      </div>
    )
  }

  const activatedDate = site.networkSiteLaunchDate
    ? new Date(site.networkSiteLaunchDate * 1000).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : null

  const structureType = site.indoorOrOutdoorSite === 'outdoor'
    ? site.outdoorVerticalStructureType || '—'
    : site.indoorStructureType || '—'

  const technologies: NetworkTechnology[] = Array.isArray(site.networkTechnologies)
    ? site.networkTechnologies.map((t: any) =>
        typeof t === 'string'
          ? { technology: t, spectrum: '', band: '', frequencyChannel: '', numberOfSectors: 0, antennaType: '' }
          : t
      )
    : []

  const lat = site.siteGeoLocation?.latitude
  const lng = site.siteGeoLocation?.longitude
  const hasLocation = !!lat && !!lng

  return (
    <>
      {/* Print styles — hidden UI chrome, clean layout for PDF */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          iframe { break-inside: avoid; }
        }
      `}</style>

      <div className="space-y-6" id="site-profile-print">

        {/* Back button + Header */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <Link to="/sensnet/network-sites">
              <Button variant="ghost" size="sm" className="gap-2 -ml-2">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.print()}
              className="gap-2 no-print"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </Button>
          </div>
          <ProfileHeader
            title={site.name}
            subtitle={activatedDate ? `Activated: ${activatedDate}` : undefined}
            badge={activatedDate ? 'Active' : undefined}
            subtitleIcon={<Radio className="h-4 w-4" />}
          />
        </div>

        {/* Location Information (left) | Site Image (right) */}
        <div className="grid grid-cols-1 md:grid-cols-2 items-stretch gap-6">
          <ProfileSection
            title="Location Information"
            icon={<MapPin className="h-4 w-4" />}
            variant="dark"
          >
            <div className="space-y-4 p-5">
              <div>
                <p className="text-xs text-muted-foreground">Corridor Name</p>
                <p className="font-medium">{corridorName || '—'}</p>
                <div className="mt-2 border-b border-slate-200" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Address</p>
                <p className="font-medium">{site.siteAddress || '—'}</p>
                <div className="mt-2 border-b border-slate-200" />
              </div>
              {site.locationNotes && (
                <div>
                  <p className="text-xs text-muted-foreground">Location Notes</p>
                  <p className="text-sm">{site.locationNotes}</p>
                </div>
              )}
            </div>
          </ProfileSection>

          {/* Site Image card */}
          <ProfileSection title="Site Image" icon={<MapPin className="h-4 w-4" />} variant="dark" className="flex flex-col">
            <div className="flex-1 overflow-hidden bg-slate-100">
              {effectivePictures.length > 0 ? (
                <img
                  src={effectivePictures[0]}
                  alt={site.name}
                  className="h-full w-full object-cover"
                  style={{ minHeight: 240 }}
                />
              ) : (
                <div className="flex h-full min-h-[240px] flex-col items-center justify-center gap-2 text-muted-foreground">
                  <MapPin className="h-8 w-8 opacity-30" />
                  <span className="text-sm">No image available</span>
                </div>
              )}
            </div>
          </ProfileSection>
        </div>

        {/* Map */}
        {hasLocation && (
          <ProfileSection
            title="Map"
            icon={<MapPin className="h-4 w-4" />}
            variant="dark"
          >
            <div className="overflow-hidden" style={{ height: 320 }}>
              <iframe
                title={`Map for ${site.name}`}
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01},${lat - 0.01},${lng + 0.01},${lat + 0.01}&layer=mapnik&marker=${lat},${lng}`}
                className="h-full w-full border-0"
              />
            </div>
          </ProfileSection>
        )}

        {/* Vertical Structure Information */}
        <ProfileSection
          title="Vertical Structure Information"
          icon={<Radio className="h-4 w-4" />}
          variant="dark"
        >
          <div className="grid grid-cols-3 divide-x divide-slate-200 p-5">
            <div className="pr-4">
              <p className="text-xs text-muted-foreground">Structure Type</p>
              <p className="font-medium capitalize">{structureType}</p>
            </div>
            <div className="px-4">
              <p className="text-xs text-muted-foreground">Structure Height</p>
              <p className="font-medium">
                {site.verticalStructureHeight ? `${site.verticalStructureHeight} meters` : '—'}
              </p>
            </div>
            <div className="pl-4">
              <p className="text-xs text-muted-foreground">Site Type</p>
              <p className="font-medium capitalize">{site.indoorOrOutdoorSite}</p>
            </div>
          </div>
        </ProfileSection>

        {/* Network Information */}
        {technologies.length > 0 && (
          <ProfileSection
            title="Network Information"
            icon={<Wifi className="h-4 w-4" />}
            variant="dark"
          >
            <div className="space-y-4 p-5">
              <div>
                <p className="mb-2 text-xs text-muted-foreground">Available Technologies</p>
                <div className="flex flex-wrap gap-2">
                  {technologies.map((t) => (
                    <span
                      key={t.technology}
                      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${TECH_COLORS[t.technology] || 'bg-slate-200 text-slate-800'}`}
                    >
                      {t.technology}
                    </span>
                  ))}
                </div>
              </div>

              {technologies.map((tech) => (
                <div key={tech.technology} className="rounded-lg border border-slate-200 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Radio className="h-4 w-4 text-muted-foreground" />
                      <span className="font-semibold">{tech.technology} Details</span>
                    </div>
                    <Badge className="bg-green-500 text-white hover:bg-green-500 text-xs">Active</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                    {tech.band && (
                      <div>
                        <p className="text-xs text-muted-foreground">Spectrum Band</p>
                        <p className="text-sm font-medium">{tech.band}</p>
                      </div>
                    )}
                    {tech.frequencyChannel && (
                      <div>
                        <p className="text-xs text-muted-foreground">Frequency Channel</p>
                        <p className="text-sm font-medium">{tech.frequencyChannel}</p>
                      </div>
                    )}
                    {tech.antennaType && (
                      <div>
                        <p className="text-xs text-muted-foreground">Antenna Information</p>
                        <p className="text-sm font-medium">{tech.antennaType}</p>
                      </div>
                    )}
                    {tech.numberOfSectors > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground">Number of Sectors</p>
                        <p className="text-sm font-medium">
                          {tech.numberOfSectors} Sector{tech.numberOfSectors !== 1 ? 's' : ''}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ProfileSection>
        )}

        {/* Backhaul Information */}
        <ProfileSection
          title="Backhaul Information"
          icon={<Radio className="h-4 w-4" />}
          variant="dark"
        >
          <div className="grid grid-cols-3 divide-x divide-slate-200 p-5">
            {BACKHAUL_DISPLAY.map((b) => {
              const isAvailable = site.backhaulCapability === b.key
              return (
                <div key={b.key} className="px-4 first:pl-0 last:pr-0">
                  <div className="mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <span>{b.icon}</span>
                      <span className="text-sm font-medium">{b.label}</span>
                    </div>
                    {isAvailable
                      ? <CheckCircle2 className="h-4 w-4 text-green-500" />
                      : <Circle className="h-4 w-4 text-slate-300" />
                    }
                  </div>
                  <p className="text-xs text-muted-foreground">{isAvailable ? b.detail : 'Not Available'}</p>
                  <p className="mt-1 text-xs text-slate-400">{b.role}</p>
                </div>
              )
            })}
          </div>
        </ProfileSection>


      </div>
    </>
  )
}
